import { observable, syncState } from "@legendapp/state";

import { authClient } from "~/libs/auth.lib";
import { trpc } from "~/libs/trpc.lib";
import { syncedCrudPersist } from "../../libs/legend-state";
import { user$ } from "../auth";
import type { OrganizationInvitation } from "./organization-invitations.types";

export const organizationInvitations$ = observable(
  syncedCrudPersist<OrganizationInvitation>({
    persist: {
      name: "organization-invitations",
    },
    waitFor: user$,
    list: async () => {
      // Better Auth's org plugin gates `listUserInvitations` on
      // `session.user.emailVerified` (see PR #9577 — the
      // `requireEmailVerificationOnInvitation` default flipped to `true`
      // and the gate covers list/get as well as accept/reject). Skipping
      // the call avoids surfacing a confusing error toast on every signed-
      // in page for unverified accounts; the `/onboard/verify-email`
      // wizard step is what asks them to verify before they can finish
      // onboarding.
      if (user$.emailVerified.get() !== true) return [];

      const { data, error } =
        await authClient.organization.listUserInvitations();

      if (error) {
        throw new Error(error.message, { cause: error });
      }

      const pendingInvitations =
        data?.filter((inv) => inv.status === "pending") ?? [];

      const orgs = await trpc.organizationInvitations.getDetails.query({
        organizationIds: pendingInvitations.map((inv) => inv.organizationId),
      });

      const orgsMap = new Map(orgs.map((org) => [org.id, org]));

      return pendingInvitations.map((inv) => {
        const org = orgsMap.get(inv.organizationId);
        return {
          ...inv,
          organizationLogo: org?.logo,
          membersCount: org?.membersCount,
        };
      });
    },
  }),
);

// Re-hydrate the store on the false→true `emailVerified` transition so
// pending invitations appear right after a user clicks the verification
// link, without requiring a page refresh. Better Auth's session atom
// re-emits when `/api/auth/verify-email` flips the column, which causes
// `user$.emailVerified` to update; we just react to that.
let lastEmailVerified: boolean | undefined;
user$.emailVerified.onChange(({ value }) => {
  const next = value === true;
  if (lastEmailVerified === false && next) {
    syncState(organizationInvitations$).sync();
  }
  lastEmailVerified = next;
});
